import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Plus, Trash2, Eye, Building2, ChevronDown, ChevronUp, Database, Search, MapPin, Loader2, Upload, FileSpreadsheet, Download, FileText, Shield, CheckSquare, Square, CheckCircle, AlertTriangle, Crown, FileBarChart, Calendar, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useTierAccess } from '../hooks/useTierAccess';
import { UpgradePrompt } from '../components/UpgradePrompt';

// Enhanced Report Frequency Options
const ENHANCED_FREQUENCY_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

// Dataset display names
const DATASET_LABELS = {
  dob_violations: 'DOB Violations',
  fire_violations: 'Fire Violations',
  ecb_violations: 'ECB Violations',
  permits: 'Applications Filed',
  mta: 'MTA',
  dot_violations: 'DOT Violations',
  dob_now: 'DOB NOW',
  bsa_applications: 'BSA Applications'
};

// Address Autocomplete Component
function AddressAutocomplete({ value, onChange, onSelect, placeholder = "Start typing an address..." }) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch address suggestions from NYC GeoSearch API
  const fetchSuggestions = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 3) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `https://geosearch.planninglabs.nyc/v2/autocomplete?text=${encodeURIComponent(searchQuery)}`
      );
      const data = await response.json();
      
      if (data.features) {
        const results = data.features.map(f => ({
          address: f.properties.label,
          borough: f.properties.borough || '',
          block: f.properties.pad_bbl ? f.properties.pad_bbl.substring(1, 6).replace(/^0+/, '') : '',
          lot: f.properties.pad_bbl ? f.properties.pad_bbl.substring(6).replace(/^0+/, '') : '',
          bbl: f.properties.pad_bbl || '',
          coordinates: f.geometry?.coordinates
        }));
        setSuggestions(results);
        setShowSuggestions(true);
      }
    } catch (err) {
      console.error('Address lookup failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setQuery(newValue);
    onChange(newValue);

    // Debounce the API call
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);
  };

  const handleSelect = (suggestion) => {
    setQuery(suggestion.address);
    setSuggestions([]);
    setShowSuggestions(false);
    onSelect(suggestion);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          value={query}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          className="input-dark pl-10 pr-8"
          data-testid="address-autocomplete-input"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-400 animate-spin" />
        )}
      </div>
      
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-zinc-900 border border-white/10 rounded-lg shadow-xl max-h-60 overflow-y-auto">
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => handleSelect(s)}
              className="w-full px-4 py-3 text-left hover:bg-white/10 transition-colors flex items-start gap-3 border-b border-white/5 last:border-0"
              data-testid={`address-suggestion-${i}`}
            >
              <MapPin className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{s.address}</p>
                <p className="text-xs text-zinc-500">
                  {s.borough && `${s.borough} • `}
                  {s.block && s.lot && `Block ${s.block}, Lot ${s.lot}`}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function WatchlistPage() {
  const { user, api } = useAuth();
  const { canAccess, isAdmin } = useTierAccess();
  const hasAccess = isAdmin || canAccess('watchlist');
  const [watchlists, setWatchlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvWatchlistName, setCsvWatchlistName] = useState('');
  const [csvUploading, setCsvUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [newWatchlist, setNewWatchlist] = useState({
    name: '',
    properties: [{ address: '', block: '', lot: '', datasets: ['dob_violations'] }]
  });
  
  // Property violations view state
  const [expandedWatchlist, setExpandedWatchlist] = useState(null);
  const [propertyViolations, setPropertyViolations] = useState({});
  const [loadingViolations, setLoadingViolations] = useState({});
  
  // Report generation state
  const [selectedProperties, setSelectedProperties] = useState({}); // { watchlistId: [propertyIndices] }
  const [generatingReport, setGeneratingReport] = useState(false);
  
  // ACRIS Owner Lookup state
  const [ownerLookupOpen, setOwnerLookupOpen] = useState(false);
  const [ownerLookupProperty, setOwnerLookupProperty] = useState(null);
  const [ownerLookupLoading, setOwnerLookupLoading] = useState(false);
  const [ownerData, setOwnerData] = useState(null);
  const [portfolioLoading, setPortfolioLoading] = useState(false);
  const [portfolioProperties, setPortfolioProperties] = useState([]);
  const [addingToWatchlist, setAddingToWatchlist] = useState(false);
  
  // Enhanced report generation state
  const [generatingEnhancedReport, setGeneratingEnhancedReport] = useState({});
  
  // Schedule enhanced report dialog state
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleDialogWatchlist, setScheduleDialogWatchlist] = useState(null);
  const [creatingSchedule, setCreatingSchedule] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    name: '',
    frequency: 'monthly',
    email_recipients: [],
    emailInput: '',
  });
  
  // Check if user has enterprise tier
  const isEnterpriseTier = user?.tier?.includes('enterprise') || user?.role === 'admin';

  // Get user's available datasets
  const availableDatasets = user?.datasets_access || ['dob_violations'];

  const fetchWatchlists = async () => {
    try {
      const res = await api().get('/watchlist');
      setWatchlists(res.data);
    } catch (err) {
      if (err?.response?.status === 403) {
        // Tier restriction - handled by UI
      } else {
        console.error('Failed to fetch watchlists:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWatchlists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ACRIS Owner Lookup
  const handleOwnerLookup = async (property, watchlistId) => {
    setOwnerLookupProperty({ ...property, watchlistId });
    setOwnerLookupOpen(true);
    setOwnerData(null);
    setPortfolioProperties([]);
    setOwnerLookupLoading(true);
    
    try {
      // Parse borough from address or use block to determine
      let borough = 'manhattan'; // default
      const addr = property.address?.toLowerCase() || '';
      if (addr.includes('brooklyn') || addr.includes('bk')) borough = 'brooklyn';
      else if (addr.includes('queens') || addr.includes('qn')) borough = 'queens';
      else if (addr.includes('bronx') || addr.includes('bx')) borough = 'bronx';
      else if (addr.includes('staten')) borough = 'staten island';
      
      const res = await api().get(`/property/owner?borough=${borough}&block=${property.block}&lot=${property.lot}`);
      
      if (res.data.success) {
        setOwnerData(res.data);
        toast.success(`Found owner: ${res.data.owner.name}`);
      } else {
        toast.error(res.data.error || 'Owner not found in ACRIS records');
      }
    } catch (err) {
      console.error('Owner lookup error:', err);
      toast.error('Failed to look up owner');
    } finally {
      setOwnerLookupLoading(false);
    }
  };

  // Find all properties by owner
  const handleFindPortfolio = async () => {
    if (!ownerData?.owner?.name) return;
    
    setPortfolioLoading(true);
    setPortfolioProperties([]);
    
    try {
      const res = await api().get(`/property/owner/portfolio?owner_name=${encodeURIComponent(ownerData.owner.name)}`);
      
      if (res.data.success && res.data.properties?.length > 0) {
        setPortfolioProperties(res.data.properties);
        toast.success(`Found ${res.data.properties.length} properties owned by ${ownerData.owner.name}`);
      } else {
        toast.info('No additional properties found for this owner');
      }
    } catch (err) {
      console.error('Portfolio lookup error:', err);
      toast.error('Portfolio lookup timed out - try a shorter search');
    } finally {
      setPortfolioLoading(false);
    }
  };

  // Add portfolio properties to watchlist
  const handleAddPortfolioToWatchlist = async () => {
    if (!portfolioProperties.length || !ownerLookupProperty?.watchlistId) return;
    
    setAddingToWatchlist(true);
    
    try {
      // Get the current watchlist
      const watchlist = watchlists.find(w => w._id === ownerLookupProperty.watchlistId);
      if (!watchlist) throw new Error('Watchlist not found');
      
      // Filter out properties already in watchlist
      const existingBbls = new Set(watchlist.properties.map(p => `${p.block}-${p.lot}`));
      const newProperties = portfolioProperties.filter(p => !existingBbls.has(`${p.block}-${p.lot}`));
      
      if (newProperties.length === 0) {
        toast.info('All portfolio properties are already in this watchlist');
        setAddingToWatchlist(false);
        return;
      }
      
      // Add each property to the watchlist
      const updatedProperties = [
        ...watchlist.properties,
        ...newProperties.map(p => ({
          address: p.address || `${p.borough} Block ${p.block}, Lot ${p.lot}`,
          block: p.block,
          lot: p.lot,
          datasets: availableDatasets.slice(0, 3) // Use first 3 available datasets
        }))
      ];
      
      await api().put(`/watchlist/${watchlist._id}`, {
        name: watchlist.name,
        properties: updatedProperties
      });
      
      toast.success(`Added ${newProperties.length} properties to watchlist`);
      setOwnerLookupOpen(false);
      fetchWatchlists(); // Refresh the list
    } catch (err) {
      console.error('Add to watchlist error:', err);
      toast.error('Failed to add properties to watchlist');
    } finally {
      setAddingToWatchlist(false);
    }
  };

  // CSV Import Handler
  const handleCsvImport = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setCsvUploading(true);
    const formData = new FormData();
    formData.append('file', csvFile);
    formData.append('watchlist_name', csvWatchlistName || 'Imported Properties');
    
    try {
      const res = await api().post('/watchlist/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Imported ${res.data.properties_imported} properties`);
      if (res.data.total_errors > 0) {
        toast.warning(`${res.data.total_errors} rows had errors`);
      }
      
      setCsvDialogOpen(false);
      setCsvFile(null);
      setCsvWatchlistName('');
      fetchWatchlists();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setCsvUploading(false);
    }
  };

  // Download CSV template
  const downloadTemplate = async () => {
    try {
      const res = await api().get('/watchlist/csv-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'insite360_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  const addProperty = () => {
    setNewWatchlist(prev => ({
      ...prev,
      properties: [...prev.properties, { address: '', block: '', lot: '', datasets: ['dob_violations'] }]
    }));
  };

  const updateProperty = (index, field, value) => {
    const updated = [...newWatchlist.properties];
    updated[index][field] = value;
    setNewWatchlist(prev => ({ ...prev, properties: updated }));
  };

  const handleAddressSelect = (index, suggestion) => {
    const updated = [...newWatchlist.properties];
    updated[index] = {
      ...updated[index],
      address: suggestion.address,
      block: suggestion.block,
      lot: suggestion.lot,
      borough: suggestion.borough
    };
    setNewWatchlist(prev => ({ ...prev, properties: updated }));
    toast.success(`Auto-filled: Block ${suggestion.block}, Lot ${suggestion.lot}`);
  };

  const togglePropertyDataset = (index, dataset) => {
    const updated = [...newWatchlist.properties];
    const currentDatasets = updated[index].datasets || [];
    if (currentDatasets.includes(dataset)) {
      // Remove dataset (but keep at least one)
      if (currentDatasets.length > 1) {
        updated[index].datasets = currentDatasets.filter(d => d !== dataset);
      }
    } else {
      // Add dataset
      updated[index].datasets = [...currentDatasets, dataset];
    }
    setNewWatchlist(prev => ({ ...prev, properties: updated }));
  };

  const removeProperty = (index) => {
    if (newWatchlist.properties.length > 1) {
      setNewWatchlist(prev => ({
        ...prev,
        properties: prev.properties.filter((_, i) => i !== index)
      }));
    }
  };

  const handleCreate = async () => {
    if (!newWatchlist.name) {
      toast.error('Please enter a watchlist name');
      return;
    }
    const validProperties = newWatchlist.properties.filter(p => p.address || p.block);
    if (validProperties.length === 0) {
      toast.error('Please add at least one property');
      return;
    }

    try {
      await api().post('/watchlist', {
        name: newWatchlist.name,
        properties: validProperties
      });
      toast.success('Watchlist created!');
      setDialogOpen(false);
      setNewWatchlist({ name: '', properties: [{ address: '', block: '', lot: '', datasets: ['dob_violations'] }] });
      fetchWatchlists();
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'string' ? errorDetail : 'Failed to create watchlist';
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this watchlist?')) return;
    try {
      await api().delete(`/watchlist/${id}`);
      toast.success('Watchlist deleted');
      fetchWatchlists();
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'string' ? errorDetail : 'Failed to delete watchlist';
      toast.error(errorMsg);
    }
  };

  // Property selection for reports
  const togglePropertySelection = (watchlistId, propertyIndex) => {
    setSelectedProperties(prev => {
      const current = prev[watchlistId] || [];
      if (current.includes(propertyIndex)) {
        return { ...prev, [watchlistId]: current.filter(i => i !== propertyIndex) };
      } else {
        return { ...prev, [watchlistId]: [...current, propertyIndex] };
      }
    });
  };

  const toggleSelectAll = (watchlistId, properties) => {
    setSelectedProperties(prev => {
      const current = prev[watchlistId] || [];
      if (current.length === properties.length) {
        return { ...prev, [watchlistId]: [] };
      } else {
        return { ...prev, [watchlistId]: properties.map((_, i) => i) };
      }
    });
  };

  const getSelectedCount = (watchlistId) => {
    return (selectedProperties[watchlistId] || []).length;
  };

  // Generate report for selected properties
  const generateReport = async (watchlistId, reportType) => {
    const watchlist = watchlists.find(w => w.id === watchlistId);
    const selected = selectedProperties[watchlistId] || [];
    
    if (selected.length === 0) {
      toast.error('Please select at least one property');
      return;
    }
    
    const selectedProps = selected.map(i => watchlist.properties[i]);
    
    setGeneratingReport(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/export/watchlist-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          watchlist_name: watchlist.name,
          properties: selectedProps,
          report_type: reportType, // 'executive' or 'extended'
          include_risk_scores: true
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate report');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${watchlist.name}_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${reportType === 'extended' ? 'Extended' : 'Executive'} report generated!`);
    } catch (err) {
      console.error('Failed to generate report:', err);
      toast.error('Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  // Generate Enhanced PDF Report (Enterprise Only) - Now uses address-based endpoint
  const generateEnhancedReport = async (property, propertyKey, reportType = 'executive') => {
    if (!isEnterpriseTier) {
      toast.error('Enhanced reports require Enterprise tier');
      return;
    }
    
    // Need address and borough for the new endpoint
    const address = property.address;
    const borough = property.borough || 'Manhattan';
    
    if (!address) {
      toast.error('Unable to generate report - Address not found for this property');
      return;
    }
    
    setGeneratingEnhancedReport(prev => ({ ...prev, [propertyKey]: true }));
    
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        address: address,
        borough: borough,
        report_type: reportType
      });
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/export/enhanced-report-by-address?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to generate enhanced report');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeAddress = address.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
      const reportLabel = reportType === 'extended' ? 'Extended' : 'Executive';
      link.setAttribute('download', `Compliance_${reportLabel}_${safeAddress}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${reportLabel} report generated!`);
    } catch (err) {
      console.error('Failed to generate enhanced report:', err);
      toast.error(err.message || 'Failed to generate enhanced report');
    } finally {
      setGeneratingEnhancedReport(prev => ({ ...prev, [propertyKey]: false }));
    }
  };

  // Fetch violations for a specific property
  const fetchPropertyViolations = async (property, propertyKey) => {
    setLoadingViolations(prev => ({ ...prev, [propertyKey]: true }));
    try {
      // Use property's selected datasets, or fall back to user's access
      const datasets = property.datasets?.length > 0 ? property.datasets : (user?.datasets_access || ['dob_violations']);
      const allViolations = [];
      
      for (const dataset of datasets) {
        // Check if user has access to this dataset
        if (!user?.datasets_access?.includes(dataset)) continue;
        
        const params = new URLSearchParams({ dataset, limit: '50' });
        if (property.address) params.append('address', property.address);
        if (property.block) params.append('block', property.block);
        if (property.lot) params.append('lot', property.lot);
        
        const res = await api().get(`/violations/search?${params}`);
        allViolations.push(...res.data.data.map(v => ({ ...v, dataset })));
      }
      
      setPropertyViolations(prev => ({ ...prev, [propertyKey]: allViolations }));
    } catch (err) {
      console.error('Failed to fetch violations:', err);
      toast.error('Failed to load violations');
    } finally {
      setLoadingViolations(prev => ({ ...prev, [propertyKey]: false }));
    }
  };

  // Toggle watchlist expansion and load violations
  const toggleWatchlistExpansion = async (watchlistId) => {
    if (expandedWatchlist === watchlistId) {
      setExpandedWatchlist(null);
    } else {
      setExpandedWatchlist(watchlistId);
      const watchlist = watchlists.find(w => w.id === watchlistId);
      if (watchlist?.properties) {
        for (const prop of watchlist.properties) {
          const key = `${watchlistId}-${prop.address}-${prop.block}-${prop.lot}`;
          if (!propertyViolations[key]) {
            fetchPropertyViolations(prop, key);
          }
        }
      }
    }
  };

  // ============== SCHEDULE ENHANCED REPORT HANDLERS ==============
  
  const openScheduleDialog = (watchlist) => {
    setScheduleDialogWatchlist(watchlist);
    setScheduleForm({
      name: `${watchlist.name} - Monthly Report`,
      frequency: 'monthly',
      email_recipients: user?.email ? [user.email] : [],
      emailInput: '',
    });
    setScheduleDialogOpen(true);
  };
  
  const handleAddScheduleEmail = () => {
    const email = scheduleForm.emailInput.trim();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    if (scheduleForm.email_recipients.includes(email)) {
      toast.error('Email already added');
      return;
    }
    setScheduleForm(prev => ({
      ...prev,
      email_recipients: [...prev.email_recipients, email],
      emailInput: ''
    }));
  };
  
  const handleRemoveScheduleEmail = (email) => {
    setScheduleForm(prev => ({
      ...prev,
      email_recipients: prev.email_recipients.filter(e => e !== email)
    }));
  };
  
  const handleCreateSchedule = async () => {
    if (!scheduleForm.name) {
      toast.error('Please enter a schedule name');
      return;
    }
    if (scheduleForm.email_recipients.length === 0) {
      toast.error('Please add at least one email recipient');
      return;
    }
    
    setCreatingSchedule(true);
    try {
      await api().post('/reports/scheduled/enhanced', {
        name: scheduleForm.name,
        frequency: scheduleForm.frequency,
        email_recipients: scheduleForm.email_recipients,
        watchlist_id: scheduleDialogWatchlist.id || scheduleDialogWatchlist._id,
      });
      toast.success('Enhanced report schedule created!');
      setScheduleDialogOpen(false);
    } catch (err) {
      console.error('Failed to create schedule:', err);
      toast.error(err.response?.data?.detail || 'Failed to create schedule');
    } finally {
      setCreatingSchedule(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="watchlist-page">
        {!hasAccess ? (
          <div>
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-white">Property Watchlist</h1>
              <p className="text-zinc-400">Monitor specific properties for new violations</p>
            </div>
            <div className="glass-card rounded-xl">
              <UpgradePrompt requiredTier="pro" feature="Property Watchlist monitoring" />
            </div>
          </div>
        ) : (
        <>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Property Watchlist</h1>
            <p className="text-zinc-400">Monitor specific properties for new violations</p>
          </div>

          <div className="flex items-center gap-3">
            {/* CSV Import Dialog */}
            <Dialog open={csvDialogOpen} onOpenChange={setCsvDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-white/20 text-zinc-300 hover:bg-white/10" data-testid="import-csv-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Import CSV
                </Button>
              </DialogTrigger>
              <DialogContent className="glass-card border-white/10 max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-white flex items-center gap-2">
                    <FileSpreadsheet className="w-5 h-5 text-indigo-400" />
                    Import Properties from CSV
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className="text-zinc-300">Watchlist Name</Label>
                    <Input
                      placeholder="Imported Properties"
                      value={csvWatchlistName}
                      onChange={(e) => setCsvWatchlistName(e.target.value)}
                      className="input-dark mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-zinc-300 mb-2 block">CSV File</Label>
                    <div 
                      className="border-2 border-dashed border-white/20 rounded-lg p-6 text-center cursor-pointer hover:border-indigo-500/50 transition-colors"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept=".csv"
                        className="hidden"
                        onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                      />
                      {csvFile ? (
                        <div className="flex items-center justify-center gap-2 text-green-400">
                          <FileSpreadsheet className="w-5 h-5" />
                          <span>{csvFile.name}</span>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
                          <p className="text-zinc-400 text-sm">Click to select CSV file</p>
                          <p className="text-zinc-600 text-xs mt-1">or drag and drop</p>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-white/5 p-3 rounded-lg">
                    <p className="text-xs text-zinc-400 mb-2">Expected columns: address, block, lot, borough (optional), datasets (optional)</p>
                    <Button 
                      variant="link" 
                      size="sm" 
                      className="text-indigo-400 p-0 h-auto"
                      onClick={downloadTemplate}
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Download template
                    </Button>
                  </div>
                  
                  <Button
                    onClick={handleCsvImport}
                    disabled={!csvFile || csvUploading}
                    className="btn-primary w-full"
                  >
                    {csvUploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Import Properties
                      </>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* New Watchlist Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="create-watchlist-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Watchlist
                </Button>
              </DialogTrigger>
              <DialogContent className="glass-card border-white/10 max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-white">Create Watchlist</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className="text-zinc-300">Watchlist Name</Label>
                    <Input
                      placeholder="My Properties"
                      value={newWatchlist.name}
                      onChange={(e) => setNewWatchlist(prev => ({ ...prev, name: e.target.value }))}
                      className="input-dark mt-1"
                      data-testid="watchlist-name-input"
                    />
                  </div>

                  <div>
                    <Label className="text-zinc-300 mb-2 block">Properties</Label>
                    <div className="space-y-4">
                      {newWatchlist.properties.map((prop, i) => (
                        <div key={i} className="bg-white/5 p-4 rounded-lg space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-zinc-400">Property {i + 1}</span>
                            {newWatchlist.properties.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeProperty(i)}
                                className="text-rose-400 hover:text-rose-300 h-6 px-2"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        
                        {/* Address Autocomplete */}
                        <div>
                          <Label className="text-xs text-zinc-500 mb-1 block">Address (start typing to search NYC)</Label>
                          <AddressAutocomplete
                            value={prop.address}
                            onChange={(val) => updateProperty(i, 'address', val)}
                            onSelect={(suggestion) => handleAddressSelect(i, suggestion)}
                            placeholder="123 Main Street, Manhattan..."
                          />
                        </div>
                        
                        {/* Block & Lot (auto-filled or manual) */}
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-xs text-zinc-500 mb-1 block">Borough</Label>
                            <Input
                              placeholder="Manhattan"
                              value={prop.borough || ''}
                              onChange={(e) => updateProperty(i, 'borough', e.target.value)}
                              className="input-dark text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-zinc-500 mb-1 block">Block</Label>
                            <Input
                              placeholder="Block"
                              value={prop.block}
                              onChange={(e) => updateProperty(i, 'block', e.target.value)}
                              className="input-dark text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-zinc-500 mb-1 block">Lot</Label>
                            <Input
                              placeholder="Lot"
                              value={prop.lot}
                              onChange={(e) => updateProperty(i, 'lot', e.target.value)}
                              className="input-dark text-sm"
                            />
                          </div>
                        </div>
                        
                        {/* Auto-fill indicator */}
                        {prop.block && prop.lot && (
                          <div className="flex items-center gap-1.5 text-xs text-emerald-400">
                            <MapPin className="w-3 h-3" />
                            <span>Block {prop.block}, Lot {prop.lot} {prop.borough && `• ${prop.borough}`}</span>
                          </div>
                        )}
                        
                        {/* Dataset Selection */}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Database className="w-3 h-3 text-zinc-500" />
                            <span className="text-xs text-zinc-500">Datasets to Monitor</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {availableDatasets.map(ds => (
                              <button
                                key={ds}
                                type="button"
                                onClick={() => togglePropertyDataset(i, ds)}
                                className={`text-xs px-2 py-1 rounded transition-all ${
                                  prop.datasets?.includes(ds)
                                    ? 'bg-indigo-500/30 text-indigo-300 border border-indigo-500/50'
                                    : 'bg-white/5 text-zinc-400 border border-white/10 hover:border-white/20'
                                }`}
                              >
                                {DATASET_LABELS[ds] || ds}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={addProperty}
                    className="mt-3 text-indigo-400 hover:text-indigo-300"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Another Property
                  </Button>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button 
                    variant="ghost" 
                    onClick={() => setDialogOpen(false)}
                    className="btn-ghost"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleCreate}
                    className="btn-primary"
                    data-testid="save-watchlist-btn"
                  >
                    Create Watchlist
                  </Button>
                </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Watchlists Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="spinner" />
          </div>
        ) : watchlists.length === 0 ? (
          <div className="glass-card rounded-xl p-12 text-center">
            <Building2 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No Watchlists Yet</h3>
            <p className="text-zinc-400 mb-6">Create a watchlist to start monitoring properties</p>
            <Button onClick={() => setDialogOpen(true)} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Create Watchlist
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {watchlists.map((wl) => (
              <div key={wl.id} className="glass-card rounded-xl overflow-hidden" data-testid={`watchlist-card-${wl.id}`}>
                {/* Watchlist Header */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white">{wl.name}</h3>
                      <p className="text-sm text-zinc-400">{wl.properties?.length || 0} properties</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Property Report Button (Executive/Full) */}
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={getSelectedCount(wl.id) === 0 || generatingReport}
                            className={`border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 ${getSelectedCount(wl.id) === 0 ? 'opacity-50' : ''}`}
                            data-testid={`generate-report-${wl.id}`}
                          >
                            {generatingReport ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <FileText className="w-4 h-4 mr-1" />
                            )}
                            Report {getSelectedCount(wl.id) > 0 && `(${getSelectedCount(wl.id)})`}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-56 p-2 bg-zinc-900 border-white/10" align="end">
                          <div className="space-y-1">
                            <button
                              onClick={() => generateReport(wl.id, 'executive')}
                              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 text-left transition-colors"
                              data-testid={`watchlist-executive-report-btn`}
                            >
                              <FileText className="w-4 h-4 text-teal-400" />
                              <div>
                                <p className="text-sm text-white">Executive</p>
                                <p className="text-xs text-zinc-500">Summary + AI analysis</p>
                              </div>
                            </button>
                            <button
                              onClick={() => generateReport(wl.id, 'extended')}
                              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 text-left transition-colors"
                              data-testid={`watchlist-extended-report-btn`}
                            >
                              <Shield className="w-4 h-4 text-purple-400" />
                              <div>
                                <p className="text-sm text-white">Extended</p>
                                <p className="text-xs text-zinc-500">+ Open violations & applications detail</p>
                              </div>
                            </button>
                          </div>
                        </PopoverContent>
                      </Popover>
                      
                      {/* Schedule Enhanced Reports Button (Enterprise Only) */}
                      {isEnterpriseTier && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openScheduleDialog(wl)}
                          className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                          data-testid={`schedule-report-${wl.id}`}
                        >
                          <Calendar className="w-4 h-4 mr-1" />
                          Schedule
                        </Button>
                      )}
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleWatchlistExpansion(wl.id)}
                        className="text-indigo-400 hover:text-indigo-300"
                        data-testid={`expand-watchlist-${wl.id}`}
                      >
                        {expandedWatchlist === wl.id ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                        <span className="ml-1">{expandedWatchlist === wl.id ? 'Hide' : 'View'} Violations</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(wl.id)}
                        className="text-zinc-400 hover:text-rose-400"
                        data-testid={`delete-watchlist-${wl.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Select All / Properties List (collapsed view) */}
                  {expandedWatchlist !== wl.id && (
                    <div className="space-y-2">
                      {/* Select All */}
                      {wl.properties?.length > 0 && (
                        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-white/10">
                          <button
                            onClick={() => toggleSelectAll(wl.id, wl.properties)}
                            className="flex items-center gap-2 text-xs text-zinc-400 hover:text-white transition-colors"
                          >
                            {getSelectedCount(wl.id) === wl.properties.length ? (
                              <CheckSquare className="w-4 h-4 text-indigo-400" />
                            ) : (
                              <Square className="w-4 h-4" />
                            )}
                            {getSelectedCount(wl.id) === wl.properties.length ? 'Deselect All' : 'Select All'}
                          </button>
                          {getSelectedCount(wl.id) > 0 && (
                            <span className="text-xs text-indigo-400">
                              {getSelectedCount(wl.id)} selected
                            </span>
                          )}
                        </div>
                      )}
                      
                      {wl.properties?.map((prop, i) => (
                        <div 
                          key={i} 
                          className={`flex items-start gap-3 px-4 py-3 rounded-lg cursor-pointer transition-colors ${
                            (selectedProperties[wl.id] || []).includes(i) 
                              ? 'bg-indigo-500/20 border border-indigo-500/30' 
                              : 'bg-white/5 hover:bg-white/10'
                          }`}
                          onClick={() => togglePropertySelection(wl.id, i)}
                        >
                          <div className="flex items-center justify-center w-5 h-5 mt-0.5">
                            {(selectedProperties[wl.id] || []).includes(i) ? (
                              <CheckSquare className="w-4 h-4 text-indigo-400" />
                            ) : (
                              <Square className="w-4 h-4 text-zinc-500" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">
                              {prop.address || `Block ${prop.block}, Lot ${prop.lot}`}
                            </p>
                            {prop.datasets?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {prop.datasets.map(ds => (
                                  <span key={ds} className="text-xs bg-indigo-500/20 text-indigo-300 px-1.5 py-0.5 rounded">
                                    {DATASET_LABELS[ds] || ds}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Expanded Violations View */}
                {expandedWatchlist === wl.id && (
                  <div className="border-t border-white/10">
                    {wl.properties?.map((prop, propIndex) => {
                      const key = `${wl.id}-${prop.address}-${prop.block}-${prop.lot}`;
                      const violations = propertyViolations[key] || [];
                      const isLoading = loadingViolations[key];
                      const isGeneratingEnhanced = generatingEnhancedReport[key];
                      
                      return (
                        <div key={propIndex} className="border-b border-white/5 last:border-b-0">
                          {/* Property Header */}
                          <div className="px-6 py-3 bg-white/5">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-indigo-400" />
                                <span className="font-medium text-white">{prop.address || `Block ${prop.block}, Lot ${prop.lot}`}</span>
                                {prop.block && prop.lot && prop.address && (
                                  <span className="text-xs text-zinc-500 ml-2">Block {prop.block}, Lot {prop.lot}</span>
                                )}
                              </div>
                              {!isLoading && (
                                <div className="flex items-center gap-2">
                                  {/* Enhanced Report Dropdown (Enterprise Only) */}
                                  {isEnterpriseTier ? (
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <button
                                          disabled={isGeneratingEnhanced}
                                          className="text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors bg-amber-500/20 text-amber-400 hover:bg-amber-500/30"
                                          data-testid={`enhanced-report-btn-${propIndex}`}
                                        >
                                          {isGeneratingEnhanced ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                          ) : (
                                            <>
                                              <FileBarChart className="w-3 h-3" />
                                              Report
                                              <ChevronDown className="w-3 h-3" />
                                            </>
                                          )}
                                        </button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-64 bg-zinc-900 border-amber-500/30 p-0" align="end">
                                        <div className="p-3 border-b border-white/10">
                                          <h4 className="font-medium text-white text-sm flex items-center gap-2">
                                            <FileBarChart className="w-4 h-4 text-amber-400" />
                                            Property Report
                                          </h4>
                                        </div>
                                        <div className="p-2 space-y-1">
                                          <button
                                            onClick={() => generateEnhancedReport(prop, key, 'executive')}
                                            className="w-full text-left p-2 rounded hover:bg-white/5 transition-colors"
                                          >
                                            <div className="font-medium text-teal-400 text-sm">Executive</div>
                                            <p className="text-xs text-zinc-500 mt-0.5">
                                              Summary + AI Insite analysis
                                            </p>
                                          </button>
                                          <button
                                            onClick={() => generateEnhancedReport(prop, key, 'extended')}
                                            className="w-full text-left p-2 rounded hover:bg-white/5 transition-colors"
                                          >
                                            <div className="font-medium text-purple-400 text-sm">Extended</div>
                                            <p className="text-xs text-zinc-500 mt-0.5">
                                              + Open violations & applications detail
                                            </p>
                                          </button>
                                        </div>
                                      </PopoverContent>
                                    </Popover>
                                  ) : (
                                    <button
                                      disabled
                                      className="text-xs px-2 py-1 rounded flex items-center gap-1 bg-zinc-700/50 text-zinc-500 cursor-not-allowed"
                                      title="Enterprise tier required"
                                    >
                                      <Crown className="w-3 h-3" />
                                      Enterprise
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleOwnerLookup(prop, wl._id)}
                                    className="text-xs px-2 py-1 rounded bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 transition-colors flex items-center gap-1"
                                    data-testid={`find-owner-btn-${propIndex}`}
                                  >
                                    <Search className="w-3 h-3" />
                                    Find Owner
                                  </button>
                                  <span className={`text-xs px-2 py-1 rounded ${
                                    violations.length > 0 ? 'bg-rose-500/20 text-rose-400' : 'bg-emerald-500/20 text-emerald-400'
                                  }`}>
                                    {violations.length} violation{violations.length !== 1 ? 's' : ''}
                                  </span>
                                </div>
                              )}
                            </div>
                            {prop.datasets?.length > 0 && (
                              <div className="flex items-center gap-2 mt-2">
                                <Database className="w-3 h-3 text-zinc-500" />
                                <span className="text-xs text-zinc-500">Monitoring:</span>
                                <div className="flex flex-wrap gap-1">
                                  {prop.datasets.map(ds => (
                                    <span key={ds} className="text-xs bg-white/10 text-zinc-400 px-1.5 py-0.5 rounded">
                                      {DATASET_LABELS[ds] || ds}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Violations Table */}
                          <div className="px-6 py-3">
                            {isLoading ? (
                              <div className="flex items-center justify-center py-4">
                                <div className="spinner" />
                              </div>
                            ) : violations.length === 0 ? (
                              <p className="text-sm text-zinc-500 text-center py-4">No violations found</p>
                            ) : (
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="text-left text-zinc-500 text-xs uppercase">
                                      <th className="pb-2">ID</th>
                                      <th className="pb-2">Type</th>
                                      <th className="pb-2">Description</th>
                                      <th className="pb-2">Date</th>
                                      <th className="pb-2">Status</th>
                                      <th className="pb-2">Severity</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-white/5">
                                    {violations.slice(0, 10).map((v, vi) => (
                                      <tr key={vi} className="text-zinc-300">
                                        <td className="py-2 mono text-xs">{v.violation_id}</td>
                                        <td className="py-2">
                                          <span className="text-xs bg-white/10 px-2 py-0.5 rounded">
                                            {v.violation_type}
                                          </span>
                                        </td>
                                        <td className="py-2 max-w-xs truncate" title={v.description}>{v.description}</td>
                                        <td className="py-2 text-xs">{v.issue_date}</td>
                                        <td className="py-2">
                                          <span className={`badge-${v.status === 'Open' ? 'error' : v.status === 'Closed' ? 'success' : 'warning'} text-xs`}>
                                            {v.status}
                                          </span>
                                        </td>
                                        <td className="py-2">
                                          <span className={`badge-${v.severity === 'High' ? 'error' : v.severity === 'Medium' ? 'warning' : 'info'} text-xs`}>
                                            {v.severity}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                {violations.length > 10 && (
                                  <p className="text-xs text-zinc-500 text-center mt-2">
                                    +{violations.length - 10} more violations
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Footer */}
                <div className="px-6 py-3 bg-white/5 border-t border-white/10">
                  <p className="text-xs text-zinc-500">
                    Created {new Date(wl.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

      {/* Owner Lookup Dialog */}
      <Dialog open={ownerLookupOpen} onOpenChange={setOwnerLookupOpen}>
        <DialogContent className="glass-card border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Search className="w-5 h-5 text-indigo-400" />
              Property Owner Lookup
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Property Info */}
            {ownerLookupProperty && (
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="text-sm text-zinc-400">Looking up owner for:</p>
                <p className="text-white font-medium">
                  {ownerLookupProperty.address || `Block ${ownerLookupProperty.block}, Lot ${ownerLookupProperty.lot}`}
                </p>
                {ownerLookupProperty.block && ownerLookupProperty.lot && (
                  <p className="text-xs text-zinc-500 mt-1">
                    Block {ownerLookupProperty.block}, Lot {ownerLookupProperty.lot}
                  </p>
                )}
              </div>
            )}
            
            {/* Loading State */}
            {ownerLookupLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-indigo-400 animate-spin mr-2" />
                <span className="text-zinc-400">Searching ACRIS records...</span>
              </div>
            )}
            
            {/* Owner Result */}
            {ownerData?.success && (
              <div className="space-y-4">
                <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <span className="text-emerald-400 font-medium">Owner Found</span>
                  </div>
                  <p className="text-xl text-white font-semibold">{ownerData.owner?.name}</p>
                  {ownerData.owner?.address && (
                    <p className="text-sm text-zinc-400 mt-1">{ownerData.owner.address}</p>
                  )}
                  
                  {/* Document Details */}
                  <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-zinc-500">Document Type</p>
                      <p className="text-sm text-zinc-300">{ownerData.document?.type || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500">Recorded Date</p>
                      <p className="text-sm text-zinc-300">
                        {ownerData.document?.recorded_date 
                          ? new Date(ownerData.document.recorded_date).toLocaleDateString() 
                          : 'N/A'}
                      </p>
                    </div>
                    {ownerData.document?.amount && ownerData.document.amount !== '0' && (
                      <div>
                        <p className="text-xs text-zinc-500">Document Amount</p>
                        <p className="text-sm text-zinc-300">
                          ${parseInt(ownerData.document.amount).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Previous Owners */}
                  {ownerData.previous_owners?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <p className="text-xs text-zinc-500 mb-2">Previous Owner(s)</p>
                      {ownerData.previous_owners.map((prev, i) => (
                        <p key={i} className="text-sm text-zinc-400">{prev.name}</p>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Portfolio Discovery Section */}
                <div className="p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-lg">
                  <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-indigo-400" />
                    Portfolio Auto-Discovery
                  </h4>
                  <p className="text-sm text-zinc-400 mb-4">
                    Find all other properties owned by <span className="text-white">{ownerData.owner?.name}</span> and add them to your watchlist.
                  </p>
                  
                  {!portfolioProperties.length && (
                    <Button
                      onClick={handleFindPortfolio}
                      disabled={portfolioLoading}
                      className="btn-primary w-full"
                    >
                      {portfolioLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Searching ACRIS...
                        </>
                      ) : (
                        <>
                          <Search className="w-4 h-4 mr-2" />
                          Find All Properties by This Owner
                        </>
                      )}
                    </Button>
                  )}
                  
                  {/* Portfolio Results */}
                  {portfolioProperties.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-emerald-400">
                          Found {portfolioProperties.length} properties
                        </p>
                        <Button
                          onClick={handleAddPortfolioToWatchlist}
                          disabled={addingToWatchlist}
                          className="btn-primary"
                          size="sm"
                        >
                          {addingToWatchlist ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Adding...
                            </>
                          ) : (
                            <>
                              <Plus className="w-4 h-4 mr-2" />
                              Add All to Watchlist
                            </>
                          )}
                        </Button>
                      </div>
                      
                      <div className="max-h-48 overflow-y-auto space-y-2">
                        {portfolioProperties.slice(0, 20).map((p, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-white/5 rounded text-sm">
                            <span className="text-zinc-300">
                              {p.address || `${p.borough} Block ${p.block}, Lot ${p.lot}`}
                            </span>
                            <span className="text-xs text-zinc-500">
                              {p.borough}
                            </span>
                          </div>
                        ))}
                        {portfolioProperties.length > 20 && (
                          <p className="text-xs text-zinc-500 text-center py-2">
                            +{portfolioProperties.length - 20} more properties
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* No Owner Found */}
            {ownerData && !ownerData.success && !ownerLookupLoading && (
              <div className="p-4 bg-rose-500/10 border border-rose-500/20 rounded-lg text-center">
                <AlertTriangle className="w-8 h-8 text-rose-400 mx-auto mb-2" />
                <p className="text-rose-400 font-medium">Owner Not Found</p>
                <p className="text-sm text-zinc-400 mt-2">
                  {ownerData.error || 'No ACRIS records found for this property'}
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule Enhanced Report Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent className="bg-zinc-900 border-amber-500/30 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-400" />
              Schedule Enhanced Reports
            </DialogTitle>
          </DialogHeader>
          
          {scheduleDialogWatchlist && (
            <div className="space-y-4 mt-2">
              <div className="bg-white/5 p-3 rounded-lg">
                <p className="text-sm text-white font-medium">{scheduleDialogWatchlist.name}</p>
                <p className="text-xs text-zinc-500">
                  {scheduleDialogWatchlist.properties?.length || 0} properties will be included in each report
                </p>
              </div>
              
              {/* Schedule Name */}
              <div>
                <Label className="text-zinc-400 text-sm">Schedule Name</Label>
                <Input
                  placeholder="e.g., Monthly Portfolio Report"
                  value={scheduleForm.name}
                  onChange={(e) => setScheduleForm(prev => ({ ...prev, name: e.target.value }))}
                  className="input-dark mt-1"
                  data-testid="schedule-name-input"
                />
              </div>
              
              {/* Frequency */}
              <div>
                <Label className="text-zinc-400 text-sm">Frequency</Label>
                <Select 
                  value={scheduleForm.frequency} 
                  onValueChange={(v) => setScheduleForm(prev => ({ ...prev, frequency: v }))}
                >
                  <SelectTrigger className="input-dark mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ENHANCED_FREQUENCY_OPTIONS.map(f => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Email Recipients */}
              <div>
                <Label className="text-zinc-400 text-sm">Email Recipients</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    value={scheduleForm.emailInput}
                    onChange={(e) => setScheduleForm(prev => ({ ...prev, emailInput: e.target.value }))}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddScheduleEmail()}
                    className="input-dark flex-1"
                  />
                  <Button onClick={handleAddScheduleEmail} variant="outline" className="btn-ghost">
                    Add
                  </Button>
                </div>
                {scheduleForm.email_recipients.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {scheduleForm.email_recipients.map(email => (
                      <span key={email} className="text-xs bg-white/10 px-2 py-1 rounded flex items-center gap-1">
                        <Mail className="w-3 h-3 text-zinc-400" />
                        {email}
                        <button 
                          onClick={() => handleRemoveScheduleEmail(email)}
                          className="text-zinc-500 hover:text-rose-400 ml-1"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Info */}
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                <p className="text-xs text-amber-400">
                  Enhanced PDF reports will be automatically generated and emailed to all recipients on the selected schedule.
                </p>
              </div>
              
              {/* Submit */}
              <Button
                onClick={handleCreateSchedule}
                disabled={creatingSchedule}
                className="w-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 text-amber-400 hover:from-amber-500/30 hover:to-orange-500/30"
                data-testid="create-schedule-submit"
              >
                {creatingSchedule ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating Schedule...
                  </>
                ) : (
                  <>
                    <Calendar className="w-4 h-4 mr-2" />
                    Create Schedule
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
        </>
        )}
      </div>
    </DashboardLayout>
  );
}
